/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect } from 'react';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { ApolloProvider } from '@apollo/client';
import { useDispatch } from 'react-redux';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import i18n from '../i18n';
import { apolloClient, IS_LOCAL_ENVIRONMENT } from './constants/environment';
import ErrorBoundary from './components/ErrorBoundary';
import DigitalLeads from './components/DigitalLeads';
import usePrepareConfig from './hooks/usePrepareConfig';
import useDetectDevice from './hooks/useDetectDevice';
import { setAppConfig } from './redux/appConfigReducer';
import { ConfigTypes } from './types';
import {
  JSONoffersBonusCredit,
  JSONoffersDataInfoDealer,
  JSONoffersDataInfoNoDealer,
} from '../../mocks/ctaDataInfo';
import { setUserInputs } from './redux/inputsReducer';

declare global {
  interface Window {
    ss: {
      visitor: {
        getMarketingCloudVisitorID(): string;
      };
    };
    adobeDataLayer: [
      {
        userData: {
          userECID: string;
        };
      },
    ];
  }
}
interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: any;
  remoteConfig: ConfigTypes;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  // @ts-ignore
  trackingService = typeof window === 'undefined'
    ? undefined
    : createTrackingService(__FEATURE_APP_NAME__),
  remoteConfig,
}: FeatureAppProps) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!trackingService) {
      return;
    }

    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;

    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testRemoteConfig = {
    market: 'USA',
    dealerId: '25A70',
    vehicleId: 'VVNBMjJBMThkNWY5ZTVhYWFjMTgxNzM0ZjU5Yg=',
    vehicleType: 'used',
    modelSalesCode: '',
    presetForm: 'contact-dealer',
    customForm: [],
  };

  const testRemoteConfig1 = {
    market: 'USA',
    dealerId: '05A13',
    vehicleId: null,
    vehicleType: null,
    modelSalesCode: '',
    presetForm: 'contact-dealer',
    customForm: [],
  };

  const testRemoteConfig2 = {
    market: 'USA',
    dealerId: '25A70',
    vehicleId: 'VVNBMDhBOTJhZjEyYmFmMTBhMGUwYTk5NzMwMA=',
    vehicleType: 'new',
    modelSalesCode: '',
    presetForm: null,
    customForm: ['topicOfInterest', 'firstName', 'lastName', 'preferredContactMethod'],
  };

  const testRemoteConfig3 = {
    market: 'USA',
    dealerId: null,
    vehicleId: null,
    vehicleType: null,
    modelSalesCode: '',
    presetForm: '',
    customForm: [],
  };

  const testRemoteConfig4 = {
    market: 'USA',
    dealerId: '25A70',
    vehicleId: null,
    vehicleType: null,
    modelSalesCode: '',
    presetForm: 'contact-dealer',
    customForm: ['topicOfInterest', 'firstName', 'lastName', 'preferredContactMethod'],
    departmentName: 'Test dep name',
    phoneNumberCMS: 4385301015,
    tcpaConsent: '<p> TCPA consent</p>',
    ccpaDisclaimer:
      '<p>You requested to receive information from Audi although you may have previously opted out of the sale of your personal information.<br>&nbsp;<br>Based on the California Consumer Privacy Act, by submitting this information you are now giving your express permission for this information to be shared with our authorized dealers.&nbsp;</p>',
  };

  const preparedConfig = usePrepareConfig(IS_LOCAL_ENVIRONMENT ? testRemoteConfig4 : remoteConfig);

  // detect the device type
  const deviceType = useDetectDevice();

  // Effect to grab config from AEM and push to Reducer
  useEffect(() => {
    dispatch(setAppConfig({ ...preparedConfig, device: deviceType }));
  }, [deviceType, dispatch, preparedConfig]);

  // When we have pre-set Zip code (for Offers page), we pre-set it into the input reducer

  useEffect(() => {
    if (!preparedConfig.isOfferPage && !preparedConfig.offers?.zip) return;
    dispatch(setUserInputs({ inputName: 'zipCode', inputValue: preparedConfig.offers?.zip }));
  }, [dispatch, preparedConfig.isOfferPage, preparedConfig.offers?.zip]);

  // Effect to get the vehicle data and pass it to Reducer
  // useEffect(() => {}, []);

  const testButton = (
    <button
      id="digital-leads"
      type="button"
      data-info='{"vehicleId": "WAU1CAGH4SA020193", "vehicleType": "new", "dealerId": "25A70", "presetForm":"contact-dealer"}'
    >
      open US VLP vehicle 1
    </button>
  );

  const testButton1 = (
    <button
      id="digital-leads"
      type="button"
      data-info='{"vehicleId": "WAU1CAGH4SA020193", "vehicleType": "new", "dealerId": "25A70", "presetForm":"contact-dealer"}'
    >
      open US VLP vehicle 2
    </button>
  );

  const testButton2 = (
    <button id="digital-leads" type="button">
      open
    </button>
  );
  const testButton3 = (
    <button type="button">
      <a href="#contact-dealer_general"> open hash</a>
    </button>
  );

  const testButtonOffersDealer = (
    <button type="button" id="digital-leads" data-info={JSONoffersDataInfoDealer}>
      Open Offers DL Dealer
    </button>
  );

  const testButtonOffersNoDealer = (
    <button type="button" id="digital-leads" data-info={JSONoffersDataInfoNoDealer}>
      Open Offers DL no Dealer
    </button>
  );

  const testButtonCreditBonus = (
    <button type="button" id="digital-leads" data-info={JSONoffersBonusCredit}>
      Open Offers DL with Credit Bonus
    </button>
  );

  const testButtonCA = (
    <button
      id="digital-leads"
      type="button"
      data-info='{"vehicleId": "Q0FOOTQ3MDg4OTA2QQ=", "vehicleType": "new", "dealerId": "94708", "presetForm":"contact-dealer", "market":"ca"}'
    >
      open CA VLP vehicle 1
    </button>
  );

  const testButtonLegalDisclaimer = (
    <button
      id="digital-leads"
      type="button"
      data-info='{"vehicleId": "VVNBMDhCMzJlNDQ3MGM1OWFjMTgwOTE5YWM4ZA=", "vehicleType": "new", "dealerId": "25A70", "presetForm":"contact-dealer"}'
    >
      Configurable Legal Disclaimer
    </button>
  );

  const testButtonTradeInForm = (
    <button
      id="digital-leads"
      type="button"
      data-info='{"vehicleId": "Q0FOOTQ3MDg4OTA2QQ=", "vehicleType": "new", "dealerId": "94708", "presetForm":"tradeIn", "market":"ca"}'
    >
      open CA Trade in form
    </button>
  );

  const testButtonTradeInFormNoVehicle = (
    <button
      id="digital-leads"
      type="button"
      data-info='{ "dealerId": "94708", "presetForm":"tradeIn", "market":"ca"}'
    >
      open CA Trade in form - unkown inventory
    </button>
  );

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <ApolloProvider client={apolloClient}>
            <div className="resetWrapper">
              {IS_LOCAL_ENVIRONMENT && testButton}
              {IS_LOCAL_ENVIRONMENT && testButton1}
              {IS_LOCAL_ENVIRONMENT && testButton2}
              {IS_LOCAL_ENVIRONMENT && testButton3}
              {IS_LOCAL_ENVIRONMENT && testButtonOffersDealer}
              {IS_LOCAL_ENVIRONMENT && testButtonOffersNoDealer}
              {IS_LOCAL_ENVIRONMENT && testButtonCreditBonus}
              {IS_LOCAL_ENVIRONMENT && testButtonCA}
              {IS_LOCAL_ENVIRONMENT && testButtonLegalDisclaimer}
              {IS_LOCAL_ENVIRONMENT && testButtonTradeInForm}
              {IS_LOCAL_ENVIRONMENT && testButtonTradeInFormNoVehicle}
              <DigitalLeads />
            </div>
          </ApolloProvider>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
